/**
 * spec 181 (CC-53473) — the list-page Columns picker must always leave the user a
 * way back.
 *
 * Before this, a user on a non-report list page (Report Queue, Recurring
 * schedules, Matters, Contacts…) could untick every column. The table then
 * rendered nothing, the grid card shrank to its toolbar row, and the Columns
 * menu — an absolute child of a card that is `overflow-hidden` — was clipped by
 * that short card, so none of its checkboxes could be clicked. Visibility is
 * persisted per grid, so a reload brought the user straight back to the same
 * blank list.
 *
 * DataGrid now does three things, and these two helpers are the pure halves:
 *   1. the picker is a PopoverMenu (portal + position: fixed), so no card can
 *      clip it however short the card is;
 *   2. the last visible data column cannot be unticked (`lastVisibleColumnId`);
 *   3. a stored "every column hidden" map — which exists in the wild, written
 *      before (2) — is read as "nothing hidden" (`recoverEveryColumnHidden`).
 *
 * All of it is confined to the list-page picker. Report grids (reportChrome)
 * keep their own Display View popover, which was never clipped, and their
 * stored visibility is read exactly as before.
 */

/** TanStack id of DataGrid's row-selection column (`enableSelection`). Not data. */
export const SELECTION_COLUMN_ID = '_select'

/**
 * The id of the one data column still visible, or null when zero or several are.
 *
 * `columns` is TanStack's leaf-column list (`table.getAllLeafColumns()`). The
 * row-selection column is not counted: a grid showing only its checkboxes is as
 * blank as a grid showing nothing, so it must not satisfy the guard.
 */
export function lastVisibleColumnId(columns) {
  const visible = (columns || []).filter((c) => c.id !== SELECTION_COLUMN_ID && c.getIsVisible())
  return visible.length === 1 ? visible[0].id : null
}

/**
 * A stored visibility map that hides EVERY one of `columnIds` reads as `{}` —
 * the grid's default, every column shown. Anything else is returned untouched,
 * including a map that hides all but one column, a map naming only columns the
 * grid no longer has, and a non-object (which the caller already had to cope
 * with).
 *
 * Returning `{}` rather than flipping each entry to `true` matters: DataGrid
 * writes its visibility back to the store on mount, so `{}` is also what heals
 * the stored preference, leaving nothing behind that could re-hide a column.
 */
export function recoverEveryColumnHidden(stored, columnIds) {
  if (!stored || typeof stored !== 'object') return stored
  const ids = (columnIds || []).filter((id) => id != null && id !== SELECTION_COLUMN_ID)
  if (ids.length === 0) return stored
  return ids.every((id) => stored[id] === false) ? {} : stored
}
